import { createContext, useContext, useMemo } from 'react'
import type { FilterFieldKey, FilterValue } from '@/lib/types/filter'
import { FILTER_FIELD_DEFINITIONS } from '@/lib/filters'
import { useCycleWorkItems, useTaskLabels, useTasks } from '@/lib/db/rxdb/hooks'

/**
 * The projects a filter bar filters over (one project, or a space and its
 * projects), so value pickers can count against the same tasks.
 */
export const FilterScopeContext = createContext<ReadonlySet<string> | null>(null)

/**
 * How many tasks in scope carry each value of a field (design 9b: "Backlog 5,
 * Planned 3…"). Counts top-level, unarchived tasks and ignores the other
 * filters, so a number says what picking that value alone would show.
 * Returns null outside a filter bar.
 */
export function useFilterValueCounts(fieldKey: FilterFieldKey): Map<FilterValue, number> | null {
  const scope = useContext(FilterScopeContext)
  const { tasks } = useTasks({ isDeleted: false })
  const { taskLabels } = useTaskLabels()
  const { cycleWorkItems } = useCycleWorkItems()

  return useMemo(() => {
    if (!scope) return null
    const inScope = tasks.filter(
      (task) => !!task.projectId && scope.has(task.projectId) && !task.isArchived && !task.parentId,
    )
    const counts = new Map<FilterValue, number>()
    const bump = (v: FilterValue) => counts.set(v, (counts.get(v) ?? 0) + 1)

    if (fieldKey === 'labels' || fieldKey === 'cycle') {
      const ids = new Set(inScope.map((t) => t.id))
      if (fieldKey === 'labels') {
        for (const tl of taskLabels) if (ids.has(tl.taskId)) bump(tl.labelId)
      } else {
        for (const cw of cycleWorkItems) if (!cw.isDeleted && ids.has(cw.workItemId)) bump(cw.cycleId)
      }
      return counts
    }

    const def = FILTER_FIELD_DEFINITIONS[fieldKey]
    if (!def || def.fieldType !== 'select') return counts
    for (const task of inScope) {
      const value = def.getTaskValue(task)
      if (Array.isArray(value)) value.forEach(bump)
      else if (value !== null) bump(value)
    }
    return counts
  }, [scope, tasks, taskLabels, cycleWorkItems, fieldKey])
}
